import React, { useState, useEffect } from 'react';
import { getPublicReleaseNotes } from '../../api/releases';
import './WhatsNew.css';

/**
 * What's New section for Settings Modal.
 * Displays published release notes grouped by version.
 */
function WhatsNew() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedVersions, setExpandedVersions] = useState(new Set());

  useEffect(() => {
    loadReleaseNotes();
  }, []);

  const loadReleaseNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPublicReleaseNotes();
      setReleases(data.releases || []);
      // Expand the first version by default
      if (data.releases?.length > 0) {
        setExpandedVersions(new Set([data.releases[0].version]));
      }
    } catch (err) {
      setError('Failed to load release notes');
      console.error('Error loading release notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleVersion = (version) => {
    setExpandedVersions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(version)) {
        newSet.delete(version);
      } else {
        newSet.add(version);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="whats-new whats-new--loading">
        <div className="loading-spinner-small" />
        <span>Loading release notes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="whats-new whats-new--error">
        <span>{error}</span>
        <button onClick={loadReleaseNotes}>Retry</button>
      </div>
    );
  }

  if (releases.length === 0) {
    return (
      <div className="whats-new whats-new--empty">
        <p>No release notes available yet.</p>
      </div>
    );
  }

  return (
    <div className="whats-new">
      {releases.map((release) => (
        <div key={release.version} className="whats-new__version">
          <button
            className="whats-new__version-header"
            onClick={() => toggleVersion(release.version)}
            aria-expanded={expandedVersions.has(release.version)}
          >
            <span className="version-number">Version {release.version}</span>
            <span className="version-date">{release.published_at}</span>
            <span className="version-toggle">
              {expandedVersions.has(release.version) ? '▼' : '▶'}
            </span>
          </button>

          {expandedVersions.has(release.version) && (
            <ul className="whats-new__notes">
              {release.notes.map((note, idx) => (
                <li key={note.id || idx} className="whats-new__note">
                  <strong>{note.title}</strong>
                  <p>{note.description}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export default WhatsNew;
